import {
  PolicyType,
  PolicyCategory,
  PolicyEnvironmentType,
  EnvironmentTypeFilter,
  CategoryFilter,
  EmptyStateMessageType,
  PolicyTemplate,
} from '../../types';
import { getSupportedEnvironmentTypes } from '../../utils/policyTypeUtils';

/**
 * Determine the appropriate empty state message type based on current filters
 */
export function getEmptyStateMessageType(
  envTypeSelected: EnvironmentTypeFilter,
  hasPolicyTypesBeforeFilters: boolean,
  hasSearchOrCategoryFilter: boolean
): EmptyStateMessageType {
  // If there were policy types before applying search/category filters
  if (hasPolicyTypesBeforeFilters && hasSearchOrCategoryFilter) {
    return 'filtered';
  }

  // For 'all' filter, show generic message
  if (envTypeSelected === 'all') {
    return 'all-environments';
  }

  // For kubernetes-specific filter
  if (envTypeSelected === 'kubernetes') {
    return 'kubernetes';
  }

  // For docker/podman/swarm filters
  return 'docker';
}

/**
 * Filter templates based on environment type, category, and search query
 */
export function filterTemplates(
  templates: Array<PolicyTemplate>,
  environmentFilter: EnvironmentTypeFilter,
  categoryFilter: CategoryFilter,
  searchQuery: string
): Array<PolicyTemplate> {
  return templates.filter((template) => {
    // Filter by category
    if (categoryFilter !== 'all' && template.category !== categoryFilter) {
      return false;
    }

    // Filter by environment type
    if (environmentFilter !== 'all') {
      const supportedEnvTypes = getSupportedEnvironmentTypes(template.type);
      if (
        !supportedEnvTypes.includes(environmentFilter as PolicyEnvironmentType)
      ) {
        return false;
      }
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesName = template.name.toLowerCase().includes(query);
      const matchesDescription = template.description
        .toLowerCase()
        .includes(query);
      if (!matchesName && !matchesDescription) {
        return false;
      }
    }

    return true;
  });
}

/**
 * Get available environment types from visible policy types
 */
export function getAvailableEnvironmentTypes(
  policyTypes: Array<{ id: PolicyType }>
): Set<PolicyEnvironmentType> {
  const availableEnvTypes = new Set<PolicyEnvironmentType>();
  policyTypes.forEach((metadata) => {
    const supportedTypes = getSupportedEnvironmentTypes(metadata.id);
    supportedTypes.forEach((type) => availableEnvTypes.add(type));
  });
  return availableEnvTypes;
}

/**
 * Get available categories from visible policy types
 */
export function getAvailableCategories(
  policyTypes: Array<{ category: PolicyCategory }>
): Set<PolicyCategory> {
  const availableCategories = new Set<PolicyCategory>();
  policyTypes.forEach((metadata) => {
    availableCategories.add(metadata.category);
  });
  return availableCategories;
}

/**
 * Build environment filter options
 */
export function buildEnvironmentFilterOptions(
  availableEnvTypes: Set<PolicyEnvironmentType>
): Array<{ value: EnvironmentTypeFilter; label: string }> {
  const environmentTypeLabels: Record<PolicyEnvironmentType, string> = {
    kubernetes: 'Kubernetes',
    docker: 'Docker',
    podman: 'Podman',
    swarm: 'Swarm',
  };

  return [
    { value: 'all', label: 'All Policy Types' },
    ...Array.from(availableEnvTypes).map((envType) => ({
      value: envType,
      label: environmentTypeLabels[envType],
    })),
  ];
}

/**
 * Build category filter options
 */
export function buildCategoryFilterOptions(
  availableCategories: Set<PolicyCategory>
): Array<{ value: CategoryFilter; label: string }> {
  const categoryLabels: Record<PolicyCategory, string> = {
    rbac: 'RBAC',
    security: 'Security',
    setup: 'Setup',
    registry: 'Registry',
  };

  return [
    { value: 'all', label: 'All Categories' },
    ...Array.from(availableCategories).map((category) => ({
      value: category,
      label: categoryLabels[category],
    })),
  ];
}

/**
 * Get display name for environment type
 */
export function getEnvironmentTypeDisplayName(envType: string): string {
  switch (envType) {
    case 'kubernetes':
      return 'Kubernetes';
    case 'docker':
      return 'Docker';
    case 'podman':
      return 'Podman';
    case 'swarm':
      return 'Swarm';
    default:
      return envType;
  }
}
